import type { HTMLAttributes, ReactNode } from 'react';
import { Info, AlertTriangle, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert.js';
import { Button } from '../ui/button.js';
import { cn } from '../lib/utils.js';

// -----------------------------------------------------------------------------
// Banner — semantic-toned Alert wrapper with optional dismiss + action slot.
//
// This is the merge of two components that had drifted apart: apps/main's
// `Banner` (page-level system messages — required title, description, action,
// dismiss) and the design system's `Callout` (inline notes — optional title,
// children, tone-driven aria role, spread div props). They rendered the same
// thing in the same places and disagreed only on prop names, so both APIs are
// accepted here and `Callout` is kept as an alias.
//
// Tones map to semantic tokens (no raw Tailwind palette):
//   info    -> primary/10     + primary
//   warning -> sla-warn/10    + sla-warn
//   error   -> destructive/10 + destructive   (alias: danger)
//   success -> sla-ok/10      + sla-ok
//   neutral -> muted          + foreground
// -----------------------------------------------------------------------------

export type BannerTone = 'info' | 'warning' | 'error' | 'danger' | 'success' | 'neutral';

const TONE: Record<
  BannerTone,
  { icon: typeof Info; wrapper: string; iconClass: string }
> = {
  info: {
    icon: Info,
    wrapper: 'bg-primary/10 text-primary border-primary/20 [&>svg]:text-primary',
    iconClass: 'text-primary',
  },
  warning: {
    icon: AlertTriangle,
    wrapper:
      'bg-sla-warn/10 text-sla-warn border-sla-warn/25 [&>svg]:text-sla-warn',
    iconClass: 'text-sla-warn',
  },
  error: {
    icon: AlertCircle,
    wrapper:
      'bg-destructive/10 text-destructive border-destructive/25 [&>svg]:text-destructive',
    iconClass: 'text-destructive',
  },
  // `danger` is what the design system's Callout called the destructive tone,
  // `error` is what main's Banner called it. Both spellings resolve here rather
  // than making every call site pick the one its file happened to start with.
  danger: {
    icon: AlertCircle,
    wrapper:
      'bg-destructive/10 text-destructive border-destructive/25 [&>svg]:text-destructive',
    iconClass: 'text-destructive',
  },
  success: {
    icon: CheckCircle2,
    wrapper: 'bg-sla-ok/10 text-sla-ok border-sla-ok/25 [&>svg]:text-sla-ok',
    iconClass: 'text-sla-ok',
  },
  neutral: {
    icon: Info,
    wrapper:
      'bg-muted text-foreground border-border [&>svg]:text-muted-foreground',
    iconClass: 'text-muted-foreground',
  },
};

export interface BannerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Defaults to `neutral` — an untoned note, matching the old Callout. */
  tone?: BannerTone;
  title?: ReactNode;
  /** Body copy. `children` is the same slot; both may be supplied. */
  description?: ReactNode;
  children?: ReactNode;
  /** Replaces the tone's default leading icon. `null` removes it. */
  icon?: ReactNode;
  action?: ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

export function Banner({
  tone = 'neutral',
  title,
  description,
  children,
  icon,
  action,
  dismissible,
  onDismiss,
  className,
  role,
  ...rest
}: BannerProps) {
  const entry = TONE[tone];
  const Icon = entry.icon;
  // Tone-driven role: danger/warning announce as alerts, everything else
  // announces politely. Carried over from Callout — an explicit `role` wins.
  const resolvedRole =
    role ?? (tone === 'danger' || tone === 'error' || tone === 'warning' ? 'alert' : 'status');

  return (
    <Alert
      role={resolvedRole}
      className={cn(entry.wrapper, 'relative', className)}
      {...rest}
    >
      {icon === undefined ? <Icon className="h-4 w-4" /> : icon}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {title && <AlertTitle className="mb-0 font-medium">{title}</AlertTitle>}
          {(description || children) && (
            <AlertDescription className={cn('text-current/90', title && 'mt-1')}>
              {description}
              {children}
            </AlertDescription>
          )}
        </div>
        {(action || dismissible) && (
          <div className="-mt-0.5 flex shrink-0 items-center gap-1">
            {action}
            {dismissible && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-current/80 hover:bg-current/10"
                onClick={onDismiss}
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </Alert>
  );
}

/**
 * <Callout> — the name this composition had before the two were merged. Same
 * component; prefer `Banner` in new code.
 */
export { Banner as Callout };
export type { BannerProps as CalloutProps, BannerTone as CalloutTone };
